import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Upload as UploadIcon, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { SegmentedControl } from "../components/ui/segmented-control";
import { ModeTabs, type TopLevelMode } from "../components/ModeTabs";
import { UploadDropzone } from "../components/UploadDropzone";
import { useAnalyze } from "../queries/useAnalyze";
import { useImageUpload } from "../hooks/useImageUpload";

type SubMode = "single" | "custom";

const MAX_SINGLE = 12;
const MIN_SINGLE = 1;
const MAX_FILES_MULTI = 8;
const MAX_FILES_SINGLE = 1;

interface BatchFailure {
  index: number;
  fileName: string;
  message: string;
}

export function HomePage() {
  const [mode, setMode] = useState<TopLevelMode>(1);
  const [subMode, setSubMode] = useState<SubMode>("single");
  // 单字 input — controlled text input, sliced into an array.
  const [singleInput, setSingleInput] = useState<string>("永");

  // Sequential batch progress: how many we've dispatched + the index of the
  // currently-in-flight one (0-based). Cleared when not running.
  const [batchActive, setBatchActive] = useState(false);
  const [batchDone, setBatchDone] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchFailures, setBatchFailures] = useState<BatchFailure[]>([]);

  const navigate = useNavigate();

  // Cap is 1 in mode 2 / single (practice a specific set of characters on one
  // page), otherwise the multi-image cap.
  const maxFiles = mode === 2 && subMode === "single" ? MAX_FILES_SINGLE : MAX_FILES_MULTI;
  const upload = useImageUpload(maxFiles);
  const analyze = useAnalyze();

  // Build the expectedChars that go into AnalyzeRequest.
  // - Mode 1 (直接上传): no expectation, always undefined.
  // - Mode 2 single: array of 1..MAX_SINGLE chars from the input.
  // - Mode 2 custom: undefined (backend OCRs whatever is in the image).
  const expectedChars = useMemo<string[] | undefined>(() => {
    if (mode === 1) return undefined;
    if (subMode === "custom") return undefined;
    const chars = Array.from(singleInput.replace(/\s+/g, "")).slice(0, MAX_SINGLE);
    if (chars.length < MIN_SINGLE) return undefined;
    return chars;
  }, [mode, subMode, singleInput]);

  const requiresChars = mode === 2 && subMode === "single";
  const canSubmit =
    upload.files.length > 0 &&
    upload.files.every((f) => Boolean(f.imageBase64)) &&
    (!requiresChars || (expectedChars && expectedChars.length > 0)) &&
    !batchActive;

  const submit = async () => {
    if (upload.files.length === 0) return;
    if (requiresChars && (!expectedChars || expectedChars.length === 0)) return;

    const total = upload.files.length;
    setBatchTotal(total);
    setBatchDone(0);
    setBatchFailures([]);
    setBatchActive(true);

    let navigated = false;
    const failures: BatchFailure[] = [];

    try {
      for (let i = 0; i < upload.files.length; i++) {
        const f = upload.files[i];
        try {
          const data = await analyze.mutateAsync({
            imageBase64: f.imageBase64,
            expectedChars,
          });
          if (!navigated) {
            navigated = true;
            navigate(`/result/${data.sessionId}`);
          }
        } catch (err) {
          failures.push({
            index: i,
            fileName: f.file.name,
            message: err instanceof Error ? err.message : String(err),
          });
        } finally {
          setBatchDone(i + 1);
        }
      }
    } finally {
      setBatchFailures(failures);
      setBatchActive(false);
    }
  };

  const handleModeChange = (next: TopLevelMode) => {
    setMode(next);
    // Reset the upload when switching modes so we don't carry stale state.
    upload.reset();
    setBatchFailures([]);
  };

  const handleSubModeChange = (next: SubMode) => {
    setSubMode(next);
    upload.reset();
    setBatchFailures([]);
  };

  // While the batch is running, hide individual filenames from the disabled
  // hint and show the progress chip instead.
  const submitLabel = upload.files.length > 1 ? "分析全部" : "开始分析";

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3">
        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-[color:var(--surface)] px-3 py-1 text-xs text-[color:var(--text-muted)] ring-1 ring-[color:var(--border)]">
          <Sparkles className="h-3.5 w-3.5" />
          <span>多维打分 · 薄弱字追踪</span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">练字指导</h1>
          <Link
            to="/history"
            className="text-sm text-[color:var(--text-muted)] underline-offset-4 hover:text-[color:var(--text)] hover:underline"
          >
            历史记录 →
          </Link>
        </div>
        <p className="max-w-xl text-[color:var(--text-muted)]">
          上传一张或多张字帖照片，自动识别每个字并打分。直接上传由后台全量识别；选择练习可指定期望字符集。
        </p>
      </header>

      <Card>
        <CardHeader>
          <div>
            <CardTitle>1. 选择模式</CardTitle>
            <CardDescription>直接上传即传即识别；选择练习可声明期望字符集。</CardDescription>
          </div>
        </CardHeader>
        <div className="flex flex-col gap-4">
          <ModeTabs value={mode} onChange={handleModeChange} />

          {mode === 2 && (
            <div className="flex flex-col gap-2">
              <span className="text-xs text-[color:var(--text-muted)]">子模式</span>
              <SegmentedControl<SubMode>
                role="tablist"
                ariaLabel="练习子模式"
                value={subMode}
                options={[
                  { value: "single", label: "单字" },
                  { value: "custom", label: "自定义" },
                ]}
                onChange={handleSubModeChange}
                size="sm"
              />
            </div>
          )}

          {mode === 2 && subMode === "single" && (
            <SingleMode
              value={singleInput}
              onChange={setSingleInput}
              chars={expectedChars ?? []}
            />
          )}

          {mode === 2 && subMode === "custom" && (
            <p className="text-sm text-[color:var(--text-muted)]">
              自定义模式：不传递期望字符集，后台将自动识别图片中所有可见字符并打分。
            </p>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader>
          <div>
            <CardTitle>2. 上传图片</CardTitle>
            <CardDescription>
              {mode === 1
                ? "支持点击、拖拽或 Ctrl+V 粘贴图片，可一次选择多张。建议白底清晰字迹。"
                : subMode === "single"
                  ? "支持点击、拖拽或 Ctrl+V 粘贴图片，单次仅上传一张。"
                  : "支持点击、拖拽或 Ctrl+V 粘贴图片，可一次选择多张。"}
            </CardDescription>
          </div>
        </CardHeader>
        <UploadDropzone
          files={upload.files}
          onAdd={upload.add}
          onRemoveAt={upload.removeAt}
          maxFiles={maxFiles}
          disabled={batchActive}
          className={mode === 2 && subMode === "single" ? "min-h-[250px] py-6" : undefined}
        />
      </Card>

      {batchFailures.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-[color:var(--accent)]/40 bg-[color:var(--accent)]/5 px-4 py-3 text-sm text-[color:var(--accent)]"
        >
          <p className="font-medium">
            {batchFailures.length === upload.files.length
              ? `分析失败（${batchFailures.length}/${upload.files.length}）`
              : `${batchFailures.length} 张分析失败，其余已提交至历史记录`}
          </p>
          <ul className="mt-1 list-inside list-disc text-xs">
            {batchFailures.slice(0, 3).map((f) => (
              <li key={`${f.index}-${f.fileName}`}>
                {f.fileName}：{f.message}
              </li>
            ))}
            {batchFailures.length > 3 && <li>其余 {batchFailures.length - 3} 项已省略…</li>}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-[color:var(--text-muted)]">
          {batchActive
            ? `正在分析 ${batchDone} / ${batchTotal}…`
            : expectedChars && expectedChars.length > 0
              ? `本次将分析 ${expectedChars.length} 个字符`
              : "本次将自动识别图片中所有字符"}
        </span>
        <Button
          variant="default"
          size="lg"
          onClick={submit}
          disabled={!canSubmit || batchActive}
        >
          {batchActive ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              分析中 {batchDone} / {batchTotal}
            </>
          ) : (
            <>
              <UploadIcon className="h-4 w-4" />
              {submitLabel}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

interface SingleModeProps {
  value: string;
  onChange: (next: string) => void;
  chars: string[];
}

/** Sub-mode UI for mode 2 / single: text input + removable chips for the
 *  selected characters. */
function SingleMode({ value, onChange, chars }: SingleModeProps) {
  const count = chars.length;
  const removeAt = (idx: number) => {
    const next = chars.slice(0, idx).concat(chars.slice(idx + 1)).join("");
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-xs text-[color:var(--text-muted)]">
          请输入要练习的字（{count}/{MAX_SINGLE}）
        </span>
        <input
          type="text"
          value={value}
          maxLength={MAX_SINGLE}
          onChange={(e) => onChange(e.target.value)}
          placeholder="例如：永 之 不"
          className="rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-base outline-none focus:border-[color:var(--primary)]"
        />
      </label>

      {chars.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chars.map((ch, idx) => (
            <span
              key={`${ch}-${idx}`}
              className="inline-flex items-center gap-1 rounded-full border border-[color:var(--border)] bg-[color:var(--surface-2)] px-2.5 py-1 text-sm"
            >
              <span className="font-serif text-base">{ch}</span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                aria-label={`移除 ${ch}`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[color:var(--text-muted)] hover:bg-[color:var(--surface)]"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <p className="text-xs text-[color:var(--text-muted)]">
        提示：输入 1–{MAX_SINGLE} 个汉字，后台将把它们作为期望字符集去匹配图片。
      </p>
    </div>
  );
}
